// The Rhythm Lab's conga: one drum and the single-drum tumbao a conga player keeps on it, made by synthesis alone.
//
// One drum, open tone `f0`. A stroke is built from the parts a real stroke has: the SKIN's partials (decaying sines at f0 x ratio, each with its own
// gain and decay time; the open tone starts about 6% sharp and settles to f0 within 25 ms, as a struck skin does), and NOISE bursts (the hand on the head,
// band-passed, with a fast fall). The sum is scaled to a fixed peak, soft-clipped with tanh (mild), band-limited to 40 Hz - 8 kHz and levelled against the
// open tone. The attack of every part is under 1 ms. Four strokes:
//   OPEN  the ringing tone (counts 4 and 4&), with a 3 ms contact transient;
//   SLAP  the loudest and brightest, and short: 8 ms of noise at 1.5 - 5 kHz plus damped high partials; nothing audible after about 100 ms;
//   HEEL  the palm: a soft low thud, a partial near 0.55 f0 and low-passed noise; a ghost note;
//   TOE   the fingertips: a light tick of mid-band noise and a weak f0; a ghost note.
// Every number is in `CONGA` below, to be tuned by ear. All functions are pure and deterministic (the noise is a fixed hash, not Math.random).

import { butterworth, hashNoise } from './voicedsp.js';

export type CongaStroke = 'open' | 'slap' | 'heel' | 'toe';

export const CONGA_STROKES: readonly CongaStroke[] = ['open', 'slap', 'heel', 'toe'];

/** A burst of band-passed noise. */
export interface CongaNoise {
  /** Length of the burst in milliseconds; it fades out over its last quarter, so it ends without a click. */
  readonly ms: number;
  /** Pass band in Hz. A low corner of 0 leaves that side open (a low-pass); a high corner of 0.45 x the sample rate or more does too. */
  readonly band: readonly [number, number];
  /** Fall of the burst, seconds (the amplitude is 1/e of its start after this long). */
  readonly tau: number;
  /** RMS of the burst at its start, in the units of the partials' gains (the open tone's first partial is 1). */
  readonly level: number;
}

/** One damped sine partial. */
export interface CongaPartial {
  /** Frequency as a multiple of `f0`. */
  readonly ratio: number;
  readonly gain: number;
  /** Decay time constant, seconds (amplitude falls to 1/e in this time). */
  readonly decay: number;
}

export interface CongaSpec {
  /** For the interface. */
  readonly label: string;
  /** Pitch of the open tone, Hz. */
  readonly f0: number;
  /** The open tone's skin partials (frequencies are multiples of f0). */
  readonly open: readonly CongaPartial[];
  /** A struck skin starts `sharp` (a fraction, 0.06 is 6%) above its pitch and falls to it, within `settleMs` (an exponential at a quarter of that time). */
  readonly pitch: { readonly sharp: number; readonly settleMs: number };
  /** Rise time of every partial and burst, milliseconds (under 1). */
  readonly attackMs: number;
  /** The open tone's hand-contact transient. */
  readonly contact: CongaNoise;
  /** The slap: a crack of noise and damped high partials. */
  readonly slap: { readonly noise: CongaNoise; readonly partials: readonly CongaPartial[] };
  /** The heel (palm): a low partial and low-passed noise. */
  readonly heel: { readonly noise: CongaNoise; readonly partials: readonly CongaPartial[] };
  /** The toe (fingertips): a tick of mid-band noise and a weak open-pitch partial. */
  readonly toe: { readonly noise: CongaNoise; readonly partials: readonly CongaPartial[] };
  /** What a stroke is scaled to before the soft clip, against its own peak. About 1 is a light grit; more is harder. */
  readonly drive: number;
  /** Pass band after the soft clip, Hz. */
  readonly band: readonly [number, number];
  /** Peak of the loudest stroke (the slap) after band-limiting. Under 0.9. */
  readonly peak: number;
  /** Level of each stroke, in dB against the open tone (the loudest 5 ms RMS). */
  readonly levels: Readonly<Record<CongaStroke, number>>;
  /** Loudness of the open tone in the lab mix (the conga's `levels` default). */
  readonly level: number;
}

/** The numbers to tune by ear. */
export const CONGA: CongaSpec = {
  label: 'Conga',
  f0: 240,
  open: [
    { ratio: 1, gain: 1, decay: 0.18 },
    { ratio: 1.59, gain: 0.5, decay: 0.1 },
    { ratio: 2.14, gain: 0.3, decay: 0.07 },
    { ratio: 2.3, gain: 0.2, decay: 0.06 },
    { ratio: 2.65, gain: 0.15, decay: 0.05 },
  ],
  pitch: { sharp: 0.06, settleMs: 25 },
  attackMs: 0.5,
  contact: { ms: 3, band: [1000, 4000], tau: 0.002, level: 0.15 },
  slap: {
    noise: { ms: 8, band: [1500, 5000], tau: 0.025, level: 1.2 },
    partials: [
      { ratio: 2.14, gain: 0.3, decay: 0.04 },
      { ratio: 2.65, gain: 0.2, decay: 0.035 },
      { ratio: 3.6, gain: 0.15, decay: 0.03 },
    ],
  },
  heel: {
    noise: { ms: 40, band: [0, 600], tau: 0.012, level: 0.5 },
    partials: [{ ratio: 0.55, gain: 1, decay: 0.05 }],
  },
  toe: {
    noise: { ms: 100, band: [600, 2000], tau: 0.02, level: 1 },
    partials: [{ ratio: 1, gain: 0.25, decay: 0.03 }],
  },
  drive: 1.0,
  band: [40, 8000],
  peak: 0.85,
  levels: { open: 0, slap: 1, heel: -14, toe: -16 },
  level: 1,
};

/** A partial runs this many decay constants: e^-8 is -69 dB, where it is cut. */
const TAIL = 8;
/** Seeds that keep the noise of the different bursts apart. */
const SEEDS = { contact: 0, slap: 100003, heel: 200003, toe: 300007 } as const;

/** The partials and noise a stroke is made of. */
function parts(stroke: CongaStroke): { readonly partials: readonly CongaPartial[]; readonly noise: CongaNoise; readonly seed: number; readonly glide: boolean } {
  switch (stroke) {
    case 'open': return { partials: CONGA.open, noise: CONGA.contact, seed: SEEDS.contact, glide: true };
    case 'slap': return { ...CONGA.slap, seed: SEEDS.slap, glide: false };
    case 'heel': return { ...CONGA.heel, seed: SEEDS.heel, glide: false };
    case 'toe': return { ...CONGA.toe, seed: SEEDS.toe, glide: false };
  }
}

/** Seconds a stroke lasts in the buffer: its slowest partial (or its noise, if that is longer) to e^-8. */
export function congaStrokeSeconds(stroke: CongaStroke): number {
  const { partials, noise } = parts(stroke);
  return Math.max(TAIL * Math.max(...partials.map((p) => p.decay)), noise.ms / 1000);
}

/** A noise burst: hash noise, band-passed, set to unit RMS, then shaped by the attack, the fall `tau`, the fade-out over its last quarter, and `level`. */
function noiseBurst(spec: CongaNoise, seed: number, sampleRate: number, attackSamples: number): Float64Array {
  const n = Math.max(1, Math.round((spec.ms / 1000) * sampleRate));
  const x = new Float64Array(n);
  for (let k = 0; k < n; k++) x[k] = hashNoise(k + seed);
  if (spec.band[0] > 0) butterworth(x, 'high', spec.band[0], sampleRate);
  if (spec.band[1] < 0.45 * sampleRate) butterworth(x, 'low', spec.band[1], sampleRate);
  let sum = 0;
  for (let k = 0; k < n; k++) sum += x[k]! ** 2;
  const norm = sum > 0 ? spec.level / Math.sqrt(sum / n) : 0;
  const fade0 = Math.floor(0.75 * n);
  for (let k = 0; k < n; k++) {
    const fade = k < fade0 ? 1 : 0.5 * (1 + Math.cos((Math.PI * (k - fade0)) / (n - fade0)));
    x[k] = x[k]! * norm * Math.exp(-k / (spec.tau * sampleRate)) * Math.min(1, (k + 1) / attackSamples) * fade;
  }
  return x;
}

/** The stroke before the soft clip, band-limit and levelling: partials (the open tone's glide) and noise, in the units of the partials' gains. */
function rawStroke(stroke: CongaStroke, sampleRate: number): Float64Array {
  const { partials, noise, seed, glide } = parts(stroke);
  const n = Math.ceil(congaStrokeSeconds(stroke) * sampleRate);
  const out = new Float64Array(n);
  const attack = Math.max(1, Math.round((CONGA.attackMs / 1000) * sampleRate));
  const ceiling = 0.45 * sampleRate; // a partial above this would alias
  const glideTau = (CONGA.pitch.settleMs / 4000) * sampleRate; // samples: e^-4 of the excess is left when the glide has "settled"

  for (const p of partials) {
    const hz = CONGA.f0 * p.ratio * (glide ? 1 + CONGA.pitch.sharp : 1);
    if (hz > ceiling) continue;
    const tau = p.decay * sampleRate;
    const decay = Math.exp(-1 / tau);
    const glideDecay = Math.exp(-1 / glideTau);
    const w = (2 * Math.PI * CONGA.f0 * p.ratio) / sampleRate; // radians a sample at the settled pitch
    const last = Math.min(n, Math.ceil(TAIL * tau));
    let env = p.gain, bend = 1; // bend: the glide's remaining excess, from 1 to 0
    for (let k = 0; k < last; k++) {
      // the phase is the integral of the frequency f0 x ratio x (1 + sharp x bend): w k + w sharp glideTau (1 - bend)
      const phase = glide ? w * k + w * CONGA.pitch.sharp * glideTau * (1 - bend) : w * k;
      out[k]! += env * Math.sin(phase) * (k < attack ? k / attack : 1);
      env *= decay;
      bend *= glideDecay;
    }
  }

  const burst = noiseBurst(noise, seed, sampleRate, attack);
  for (let k = 0; k < Math.min(n, burst.length); k++) out[k]! += burst[k]!;
  return out;
}

/** Soft-clip and band-limit a raw stroke, in place: scaled so its peak is `drive`, then tanh. */
function finish(raw: Float64Array, sampleRate: number): Float64Array {
  let peak = 0;
  for (const v of raw) peak = Math.max(peak, Math.abs(v));
  const k = peak > 0 ? CONGA.drive / peak : 0;
  for (let i = 0; i < raw.length; i++) raw[i] = Math.tanh(raw[i]! * k);
  butterworth(raw, 'high', CONGA.band[0], sampleRate);
  butterworth(raw, 'low', CONGA.band[1], sampleRate);
  return raw;
}

/** The loudest 5 ms RMS of a stroke: its level. */
function loudness(x: ArrayLike<number>, sampleRate: number): number {
  const window = Math.max(1, Math.round(0.005 * sampleRate)), step = Math.max(1, Math.round(0.001 * sampleRate));
  let best = 0;
  for (let i = 0; i + window <= x.length; i += step) {
    let s = 0;
    for (let j = i; j < i + window; j++) s += x[j]! ** 2;
    best = Math.max(best, s);
  }
  return Math.sqrt(best / window);
}

/**
 * All four strokes at a sample rate, as played in the loop: each is levelled against the open tone by `CONGA.levels` (the loudest 5 ms RMS), then the
 * set is scaled together so that its loudest peak is `CONGA.peak`. Pure.
 */
export function congaStrokes(sampleRate: number): Readonly<Record<CongaStroke, Float32Array>> {
  const done = Object.fromEntries(CONGA_STROKES.map((s) => [s, finish(rawStroke(s, sampleRate), sampleRate)])) as Record<CongaStroke, Float64Array>;
  const reference = loudness(done.open, sampleRate);
  const gain = {} as Record<CongaStroke, number>;
  let peak = 0;
  for (const s of CONGA_STROKES) {
    const own = loudness(done[s], sampleRate);
    gain[s] = own > 0 ? (reference * 10 ** (CONGA.levels[s] / 20)) / own : 0;
    for (const v of done[s]) peak = Math.max(peak, Math.abs(v) * gain[s]);
  }
  const k = peak > 0 ? CONGA.peak / peak : 0;
  return Object.fromEntries(CONGA_STROKES.map((s) => [s, Float32Array.from(done[s], (v) => v * gain[s] * k)])) as Record<CongaStroke, Float32Array>;
}

/** One stroke, as `congaStrokes` plays it. */
export function congaStroke(stroke: CongaStroke, sampleRate: number): Float32Array {
  return congaStrokes(sampleRate)[stroke];
}
